/**
 * Endpoints du domaine merchant (marchands & QR — merchant-service) —
 * source canonique. Chemins copiés TELS QUELS de la collection Postman
 * `docs/guides/FlashPay.postman_collection.json` (préfixe gateway
 * `/gw/merchant`, guide §6.6). Ne jamais inventer un chemin.
 *
 * `@/core/api/endpoints` ré-exportera ces valeurs.
 */
export const merchantEndpoints = {
  /* ---------------------------------- Merchants --------------------------------- */
  merchants: {
    /** POST — idempotence OBLIGATOIRE (nouvelle clé par tentative métier, §4.7). */
    add: "/gw/merchant/api/v1/Merchants/add",
    /** POST — liste paginée, corps `ListRequest<MerchantFilter>`. */
    all: "/gw/merchant/api/v1/Merchants/all",
    /** GET — détail d'un marchand. */
    detail: (merchantId: string) =>
      `/gw/merchant/api/v1/Merchants/${merchantId}`,
    /**
     * POST — force le statut (query params `status` + `actingMerchantId`).
     * Rôles : flashpay-superadmin, flashpay-admin.
     */
    status: (merchantId: string) =>
      `/gw/merchant/api/v1/Merchants/${merchantId}/status`,
    /**
     * POST — met à jour le statut KYC (query param `kycStatus`).
     * Rôles : flashpay-superadmin, flashpay-admin, flashpay-service.
     */
    kycStatus: (merchantId: string) =>
      `/gw/merchant/api/v1/Merchants/${merchantId}/kyc-status`,
    /**
     * PATCH — met à jour les permissions granulaires (query params
     * `actingMerchantId` + `updatedByKeycloakId`, corps `{ permissions: [...] }`).
     */
    permissions: (merchantId: string) =>
      `/gw/merchant/api/v1/Merchants/${merchantId}/permissions`,
    /**
     * POST — lie un portefeuille de reversement (query param `walletId`).
     * Rôles : flashpay-superadmin, flashpay-admin.
     */
    settlementWallet: (merchantId: string) =>
      `/gw/merchant/api/v1/Merchants/${merchantId}/settlement-wallet`,
    /** GET — liste des sous-marchands d'un master (query `actingMerchantId`). */
    subMerchants: (masterId: string) =>
      `/gw/merchant/api/v1/Merchants/${masterId}/sub-merchants`,
    /**
     * POST — crée un sous-marchand rattaché à un master (idempotence
     * OBLIGATOIRE + query `actingMerchantId`). Rôle : flashpay-merchant
     * (permission `CREATE_SUB_MERCHANT`) ou back-office.
     */
    createSubMerchant: (masterId: string) =>
      `/gw/merchant/api/v1/Merchants/${masterId}/sub-merchants`,
  },
  /* -------------------------------- MerchantStores ------------------------------ */
  merchantStores: {
    /** POST — crée un point de vente. */
    add: "/gw/merchant/api/v1/MerchantStores/add",
    /** GET — détail d'un store. */
    detail: (storeId: string) =>
      `/gw/merchant/api/v1/MerchantStores/${storeId}`,
    /** PUT — met à jour un store. */
    update: (storeId: string) =>
      `/gw/merchant/api/v1/MerchantStores/${storeId}`,
    /** GET — stores d'un marchand. */
    byMerchant: (merchantId: string) =>
      `/gw/merchant/api/v1/MerchantStores/merchant/${merchantId}`,
  },
  /* ------------------------------------ QrCodes --------------------------------- */
  qrCodes: {
    /** POST — génère un QR (query `actingMerchantId`). Réservé au merchant lié. */
    add: (merchantId: string) =>
      `/gw/merchant/api/v1/QrCodes/merchant/${merchantId}/add`,
    /** GET — QR codes d'un marchand. */
    byMerchant: (merchantId: string) =>
      `/gw/merchant/api/v1/QrCodes/merchant/${merchantId}`,
    /**
     * GET — résolution scan-to-pay (payloadToken porté par le QR imprimé /
     * affiché). Public côté API mais notre back-office l'appelle authentifié.
     */
    byToken: (payloadToken: string) =>
      `/gw/merchant/api/v1/QrCodes/${payloadToken}`,
    /**
     * POST — révoque un QR (query params `actingMerchantId` + `reason`).
     * Rôles : merchant avec permission `REVOKE_QR_CODE` ou back-office.
     */
    revoke: (qrCodeId: string) =>
      `/gw/merchant/api/v1/QrCodes/${qrCodeId}/revoke`,
  },
  /* --------------------------- MerchantSalesAgents ----------------------------- */
  merchantSalesAgents: {
    /** GET — profil de l'agent de vente courant (rôle flashpay-merchant-sales-agent). */
    me: "/gw/merchant/api/v1/MerchantSalesAgents/me",
    /**
     * POST — crée un agent de vente d'un marchand (query `actingMerchantId`).
     * Rôle : merchant avec permission `CREATE_SALES_AGENT` ou back-office.
     */
    add: (merchantId: string) =>
      `/gw/merchant/api/v1/MerchantSalesAgents/merchant/${merchantId}/add`,
    /** GET — agents de vente d'un marchand. */
    byMerchant: (merchantId: string) =>
      `/gw/merchant/api/v1/MerchantSalesAgents/merchant/${merchantId}`,
    /** GET — détail d'un agent de vente. */
    detail: (salesAgentId: string) =>
      `/gw/merchant/api/v1/MerchantSalesAgents/${salesAgentId}`,
    /** POST — force le statut (query `status`). */
    status: (salesAgentId: string) =>
      `/gw/merchant/api/v1/MerchantSalesAgents/${salesAgentId}/status`,
  },
  /* ------------------------------ MerchantSales -------------------------------- */
  merchantSales: {
    /** POST — liste paginée des ventes, corps `ListRequest<MerchantSaleFilter>`. */
    all: "/gw/merchant/api/v1/MerchantSales/all",
    /** GET — ventes rattachées à un agent de vente. */
    bySalesAgent: (salesAgentId: string) =>
      `/gw/merchant/api/v1/MerchantSales/sales-agent/${salesAgentId}`,
    /**
     * POST — simule un événement de finalisation Kafka (`wallet-service`
     * transaction complétée). RÉSERVÉ au rôle `flashpay-service` (compte
     * technique).
     */
    simulateWalletTransactionCompleted:
      "/gw/merchant/api/v1/MerchantSales/simulate-wallet-transaction-completed",
  },
  /* ------------------------- MerchantSettlementConfigs ------------------------- */
  merchantSettlementConfigs: {
    /** GET — configuration de reversement d'un marchand. */
    byMerchant: (merchantId: string) =>
      `/gw/merchant/api/v1/MerchantSettlementConfigs/merchant/${merchantId}`,
    /**
     * PUT — met à jour la configuration (query `actingMerchantId`).
     * Rôle : merchant avec permission `MANAGE_SETTLEMENT_CONFIG` ou back-office.
     */
    update: (merchantId: string) =>
      `/gw/merchant/api/v1/MerchantSettlementConfigs/merchant/${merchantId}`,
  },
  /* --------------------------- MerchantFeeOverrides ---------------------------- */
  merchantFeeOverrides: {
    /** POST — ajoute une dérogation tarifaire (back-office). */
    add: "/gw/merchant/api/v1/MerchantFeeOverrides/add",
    /** GET — dérogations tarifaires d'un marchand. */
    byMerchant: (merchantId: string) =>
      `/gw/merchant/api/v1/MerchantFeeOverrides/merchant/${merchantId}`,
    /** DELETE — supprime une dérogation, retour au barème standard. */
    remove: (merchantFeeOverrideId: string) =>
      `/gw/merchant/api/v1/MerchantFeeOverrides/${merchantFeeOverrideId}`,
  },
} as const;
